import { Button } from "@/components/ui/button"
import { getGenderTitle, getRoleTitle } from "@/features/experts/utils/expertUtils"
import { useAuthStore } from "@/utils/stores/useAuthStore"
import { useUserStore } from "@/utils/stores/useUserStore"
import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import { handleDayOfMonth, handleHourAndMinutes, handleMonthString } from "../../../utils/Functions"
import { useAppointmentAction } from "../hooks/useAppointmentAction"
import { Appointment } from "../types/Appointment"
import { AppointmentAction } from "../types/AppointmentAction"
import { getTimeLeftLabel } from "../utils/timeHelpers"

// Add Calendly type
declare global {
    interface Window {
        Calendly: {
            initInlineWidget: (options: {
                url: string;
                parentElement: Element | null;
                prefill: {
                    email?: string;
                    name?: string;
                    firstName?: string;
                    lastName?: string;
                    phoneNumber?: string;
                    customAnswers?: Record<string, string>;
                };
                utm?: {
                    utmSource: string;
                    utmCampaign: string;
                };
            }) => void;
        };
    }
}

interface Props {
    appointment: Appointment,
}

const ConsultCard: React.FC<Props> = ({
    appointment,
}) => {
    const { auth } = useAuthStore()
    const { userData } = useUserStore()
    const [isExpanded, setIsExpanded] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [isCancelling, setIsCancelling] = useState(false)
    const [bookingDetails, setBookingDetails] = useState<any>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [showSuccess, setShowSuccess] = useState(false)

    const idToken = auth.idToken
    const email = userData.email

    const { text, urgent } = getTimeLeftLabel(
        appointment.remainingMinutes,
        appointment.remainingHours,
        appointment.remainingDays
    )

    const handleAppointmentAction = useAppointmentAction(email, appointment.expert.role)

    // Load Calendly script when needed
    useEffect(() => {
        if (isEditing || isCancelling) {
            const script = document.createElement('script');
            script.src = 'https://assets.calendly.com/assets/external/widget.js';
            script.async = true;
            script.setAttribute('data-locale', 'it');
            document.body.appendChild(script);

            return () => {
                // Cleanup script
                if (document.body.contains(script)) {
                    document.body.removeChild(script);
                }
            };
        }
    }, [isEditing, isCancelling]);

    // Initialize Calendly widget when script is loaded
    useEffect(() => {
        if ((isEditing || isCancelling) && window.Calendly) {
            window.Calendly.initInlineWidget({
                url: isEditing ? appointment.editUrl : appointment.cancelUrl,
                parentElement: document.querySelector('.calendly-inline-widget'),
                prefill: {
                    email: userData.email || '',
                    name: userData.name && userData.familyName ? `${userData.name} ${userData.familyName}` : '',
                    firstName: userData.name || '',
                    lastName: userData.familyName || '',
                    phoneNumber: userData.phoneNumber || '',
                    customAnswers: {
                        "1": userData.email || '',
                        "2": userData.name || '',
                        "3": userData.phoneNumber || ''
                    }
                },
                utm: {
                    utmSource: "Holifya",
                    utmCampaign: isEditing ? "EditAppointment" : "CancelAppointment"
                }
            });
        }
    }, [isEditing, isCancelling, userData, appointment.editUrl, appointment.cancelUrl]);

    const handleToggleExpand = () => {
        if (isExpanded) {
            setIsExpanded(false);
            setIsEditing(false);
            setIsCancelling(false);
            setShowSuccess(false);
        } else {
            setIsExpanded(true);
        }
    };

    const handleEditClick = () => {
        setIsEditing(true);
        setIsCancelling(false);
        setShowSuccess(false);
    };

    const handleCalendlyEvent = useCallback((event: any) => {
        console.log('Received Calendly event:', event.data);
        
        if (event.data.event === 'calendly.event_scheduled') {
            setIsLoading(true);
            // Fetch booking details using the event URI
            fetch(event.data.payload.event.uri, {
                headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                }
            })
            .then(response => response.json())
            .then(data => {
                setBookingDetails(data);
                setIsLoading(false);
                setShowSuccess(true);
                toast.success(isEditing ? 'Appuntamento modificato con successo' : 'Appuntamento annullato con successo', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            })
            .catch(error => {
                console.error('Error fetching booking details:', error);
                setIsLoading(false);
                toast.error('Si è verificato un errore. Riprova più tardi.', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            });
        } else if (event.data.event === 'calendly.profile_page_viewed') {
            console.log('Calendly profile page viewed');
        } else if (event.data.event === 'calendly.event_type_viewed') {
            console.log('Calendly event type viewed');
        }
    }, [idToken, isEditing]);

    useEffect(() => {
        window.addEventListener('message', handleCalendlyEvent);
        return () => {
            window.removeEventListener('message', handleCalendlyEvent);
        };
    }, [handleCalendlyEvent]);

    const onCancelAppointmentClick = () => {
        setIsCancelling(true);
        setIsEditing(false);
        setShowSuccess(false);
    }

    const onEditAppointmentClick = () => {
        setIsEditing(true);
        setIsCancelling(false);
        setShowSuccess(false);
    }

    const onJoinAppointmentClick = () =>
        handleAppointmentAction({
            url: appointment.connectUrl,
            action: AppointmentAction.CONNECT,
        })

    return (
        <div className={`w-full my-2.5 rounded-md flex items-center justify-center ${urgent ? "border-2 border-solid border-holifya-blue" : "border border-solid border-border shadow-platform-new"}`}>
            <div className="w-full h-full p-4 space-y-sm">
                <ConsultCardHeader 
                    urgent={urgent} 
                    text={text} 
                    onCancel={onCancelAppointmentClick}
                    isEditing={isEditing}
                    isCancelling={isCancelling}
                />
                <AppointmentDate date={appointment.date} />
                <ExpertInfo appointment={appointment} />
                {!isEditing && !isCancelling ? (
                    <AppointmentActions 
                        urgent={urgent} 
                        onEdit={onEditAppointmentClick} 
                        onJoin={onJoinAppointmentClick}
                        isEditing={isEditing}
                        isCancelling={isCancelling}
                    />
                ) : showSuccess ? (
                    <div className="w-full text-center p-4">
                        <div className="text-green-600 text-lg font-semibold mb-2">
                            {isEditing ? 'Appuntamento modificato con successo!' : 'Appuntamento annullato con successo!'}
                        </div>
                        <Button
                            onClick={handleToggleExpand}
                            className="mt-4"
                        >
                            Chiudi
                        </Button>
                    </div>
                ) : (
                    <div className="w-full">
                        <div className="flex justify-between items-center mb-4">
                            <div className="font-bold text-lg leading-6">
                                {isEditing ? 'Modifica appuntamento' : 'Annulla appuntamento'}
                            </div>
                            <Button
                                variant="white"
                                onClick={handleToggleExpand}
                                className="text-danger"
                            >
                                Chiudi
                            </Button>
                        </div>
                        <div className="text-base leading-relaxed mb-4">
                            {isEditing ? (
                                <>
                                    Stai per modificare il tuo appuntamento sulla piattaforma Calendly.<br />
                                    È importante che tu utilizzi la stessa mail con la quale hai effettuato la registrazione su Holifya.
                                </>
                            ) : (
                                <>
                                    Stai per annullare il tuo appuntamento sulla piattaforma Calendly.<br />
                                    Conferma l'annullamento nella finestra sottostante.
                                </>
                            )}
                        </div>
                        {isLoading && (
                            <div className="flex justify-center items-center p-4">
                                <div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent"></div>
                            </div>
                        )}
                        <div 
                            className="calendly-inline-widget w-full h-[600px]"
                            data-url={isEditing ? appointment.editUrl : appointment.cancelUrl}
                            data-prefill={JSON.stringify({
                                email: userData.email || '',
                                name: userData.name || '',
                                firstName: userData.name || '',
                                lastName: userData.familyName || '',
                                phone: userData.phoneNumber || '',
                                customAnswers: {
                                    "1": userData.email || '',
                                    "2": userData.name || '',
                                    "3": userData.phoneNumber || ''
                                }
                            })}
                            data-locale="it"
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

export default ConsultCard

const ConsultCardHeader: React.FC<{
    urgent: boolean
    text: string
    onCancel: () => void
    isEditing: boolean
    isCancelling: boolean
}> = ({ urgent, text, onCancel, isEditing, isCancelling }) => (
    <div className="flex justify-between items-center">
        <div className="font-bold">
            Videoconsulto |{" "}
            <span className={urgent ? "text-danger" : "text-primary"}>{text}</span>
        </div>
        {!urgent && !isEditing && !isCancelling && (
            <Button
                variant="white"
                className="font-bold text-danger text-md normal-case p-0"
                aria-label="Cancella appuntamento"
                onClick={onCancel}
            >
                Cancella
            </Button>
        )}
    </div>
)

const AppointmentDate: React.FC<{ date: string }> = ({ date }) => (
    <div className="font-normal text-[35px] leading-[35px] flex gap-2">
        <div className="font-bold">
            {handleDayOfMonth(date)} {handleMonthString(date)}
        </div>
        <div>{handleHourAndMinutes(date)}</div>
    </div>
)

const ExpertInfo: React.FC<{ appointment: Appointment }> = ({ appointment }) => {
    const expert = appointment.expert
    if (!expert) return null

    const title = getGenderTitle(expert.data.sex)
    const fullName = `${expert.data.name} ${expert.data.family_name}`
    const role = getRoleTitle(expert.role)

    return (
        <div className="w-full font-bold flex justify-between items-center">
            <div>
                <div className="text-xs">{title}</div>
                <div className="text-lg">{fullName}</div>
            </div>
            <div className="font-bold text-[14px] leading-[20px] text-holifya-blue">{role}</div>
        </div>
    )
}

const AppointmentActions: React.FC<{
    urgent: boolean;
    onEdit: () => void;
    onJoin: () => void;
    isEditing: boolean;
    isCancelling: boolean;
}> = ({
    urgent,
    onEdit,
    onJoin,
    isEditing,
    isCancelling
}) => {
    return (
        <div className="flex flex-col gap-2">
            <Button size="form" className="!text-[16px]" onClick={onJoin}>
                Collegati
            </Button>
            {!urgent && !isEditing && !isCancelling && (
                <Button
                    className="!normal-case text-base"
                    size="form"
                    variant="white"
                    onClick={onEdit}
                >
                    Modifica
                </Button>
            )}
        </div>
    );
};
